/**
 * Roadmap #146 Alt-PR C — preview environment detection.
 *
 * The QA overlay only renders on hostnames produced by the preview spawn
 * CLI: `<name>.dev.elmes.io` (and dev-time variants for testing the
 * overlay locally). Production / staging / `dev.elmes.io` itself never
 * trigger overlay rendering.
 */

const PARENT_DOMAIN = 'dev.elmes.io';

/** Hostnames that LOOK like sub-of dev.elmes.io but aren't preview spawns. */
const NON_PREVIEW_HOSTS = new Set(['dev.elmes.io', 'release.elmes.io']);

/**
 * Local-dev override via `?previewOverlay=on`. Strictly per-page-load —
 * we no longer persist to sessionStorage so a stray test query doesn't
 * leave the overlay haunting `dev.elmes.io` for the rest of the tab's
 * life. Also cleans up any legacy persisted value left over from the
 * earlier (sticky) implementation.
 */
const LEGACY_OVERRIDE_KEY = 'elmes:previewOverlay:override';

export interface ParsedPreviewContext {
  /** The full hostname (lowercase). */
  hostname: string;
  /** Spawn CLI name — e.g. `pr-142` or `branch-canvas-redesign`. */
  name: string;
  /** Detected kind from naming convention. */
  kind: 'pr' | 'branch' | 'bundle' | 'spike' | 'custom';
  /** PR number if `kind === 'pr'`. */
  prNumber?: number;
  /** PR numbers if `kind === 'bundle'`. */
  prNumbers?: number[];
  /** Slug after the kind prefix (`pr-142` → `142`, `branch-canvas-redesign` → `canvas-redesign`). */
  slug: string;
}

/** Parse the current hostname (or override) into a preview context. */
export function detectPreviewContext(): ParsedPreviewContext | null {
  if (typeof window === 'undefined') return null;

  // One-time cleanup: drop the legacy persisted-override key from any
  // browser tab that visited `?previewOverlay=on` under the previous
  // (sticky) implementation. After this runs once on a tab, the overlay
  // stops haunting non-preview hosts.
  try {
    sessionStorage.removeItem(LEGACY_OVERRIDE_KEY);
  } catch {
    /* ignore — private browsing modes etc. */
  }

  const url = new URL(window.location.href);
  // Per-page-load override only. No persistence: opening the same
  // hostname without the query flag → overlay hides.
  const override = url.searchParams.get('previewOverlay') === 'on';

  const hostname = window.location.hostname.toLowerCase();
  const isUnderParent = hostname.endsWith(`.${PARENT_DOMAIN}`);
  const isParentItself = NON_PREVIEW_HOSTS.has(hostname);

  let name: string | null = null;
  if (isUnderParent && !isParentItself) {
    name = hostname.slice(0, -1 - PARENT_DOMAIN.length);
  } else if (override) {
    // Pretend `<previewName>.dev.elmes.io` for local dev. Defaults to a
    // sentinel name so the API returns 404 (acceptable — we just want to
    // see the overlay shell render).
    name = url.searchParams.get('previewName') ?? 'pr-DEV';
  }

  if (!name) return null;
  return enrich(name, hostname);
}

function enrich(name: string, hostname: string): ParsedPreviewContext {
  // pr-{N}
  const prMatch = /^pr-(\d+)$/.exec(name);
  if (prMatch) {
    return {
      hostname,
      name,
      kind: 'pr',
      prNumber: Number(prMatch[1]),
      slug: prMatch[1],
    };
  }
  // bundle-{N1}-{N2}-...
  const bundleMatch = /^bundle-((?:\d+-)*\d+)$/.exec(name);
  if (bundleMatch) {
    return {
      hostname,
      name,
      kind: 'bundle',
      prNumbers: bundleMatch[1].split('-').map(Number),
      slug: bundleMatch[1],
    };
  }
  // branch-/spike-/anything-else
  if (name.startsWith('branch-')) {
    return { hostname, name, kind: 'branch', slug: name.slice(7) };
  }
  if (name.startsWith('spike-')) {
    return { hostname, name, kind: 'spike', slug: name.slice(6) };
  }
  return { hostname, name, kind: 'custom', slug: name };
}

/**
 * Acceptance checklist defaults. Alt-PR D replaces these with DB-backed
 * items keyed off PR labels / explicit qa_runs entries; until then the
 * overlay shows these built-in checks.
 */
export const DEFAULT_ACCEPTANCE_CHECKLIST: { id: string; label: string }[] = [
  { id: 'page-loads', label: 'Page loads without console errors' },
  { id: 'auth-flow', label: 'Login + protected route loads' },
  { id: 'happy-path', label: 'Primary changed flow works' },
  { id: 'regressions', label: 'No regressions on adjacent routes' },
];
